import React, { useEffect, useState } from "react";
import { Sparkles, Check, Loader2 } from "lucide-react";

/**
 * <AiGenerationProgress /> — Bloque 2 animated loader shown while the LLM is thinking.
 *
 * 6 steps · minimum on-screen duration to avoid a jarring blink even if the
 * network call resolves in <1s. Steps advance on a fixed cadence, and the
 * "finished" state is a controlled prop from the parent.
 */
const STEPS = [
  "Analizando tu reto",
  "Recorriendo el catálogo DCC (193 programas)",
  "Componiendo módulos coherentes",
  "Definiendo estructura y logística",
  "Calibrando duración y evaluación",
  "Preparando propuesta",
];

export default function AiGenerationProgress({ finished = false, wantsIada = "both" }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (finished) {
      setI(STEPS.length - 1);
      return;
    }
    // Advance every ~900ms; last step stays put while awaiting `finished`.
    const t = setInterval(() => {
      setI((prev) => (prev < STEPS.length - 2 ? prev + 1 : STEPS.length - 2));
    }, 900);
    return () => clearInterval(t);
  }, [finished]);

  return (
    <div
      className="p-6 rounded-2xl bg-[#0B0B0B] text-white border border-[#F26A21]/30 shadow-xl"
      data-testid="ai-generation-progress"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#F26A21]/20 border border-[#F26A21]/40 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[#F26A21] animate-pulse" aria-hidden="true" />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">
            Generando propuesta con IA
          </p>
          <p className="text-xs text-gray-300">
            {wantsIada === "yes"
              ? "Con aval IADA (+30%)"
              : wantsIada === "no"
              ? "Sin aval IADA · solo DCC"
              : "Comparando ambas opciones (con y sin aval IADA)"}
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="ai-progress-steps">
        {STEPS.map((s, idx) => {
          const done = idx < i || finished;
          const active = idx === i && !finished;
          return (
            <li
              key={s}
              className={`flex items-center gap-2 text-sm transition-opacity ${
                done ? "text-emerald-400" : active ? "text-white" : "text-gray-500"
              }`}
              data-testid={`ai-progress-step-${idx}`}
              data-state={done ? "done" : active ? "active" : "pending"}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                  done
                    ? "bg-emerald-500/20 border border-emerald-500/50"
                    : active
                    ? "bg-[#F26A21]/20 border border-[#F26A21]/60"
                    : "border border-gray-700"
                }`}
              >
                {done ? (
                  <Check className="w-3 h-3" />
                ) : active ? (
                  <Loader2 className="w-3 h-3 animate-spin text-[#F26A21]" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                )}
              </span>
              <span className={active ? "font-semibold" : ""}>{s}</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 h-1 rounded bg-white/5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#F26A21] to-[#D9541A] transition-all duration-700"
          style={{ width: `${Math.min(100, ((i + 1) / STEPS.length) * 100)}%` }}
        />
      </div>
      <p className="mt-3 text-[11px] text-gray-400 italic">
        La IA está grounded al catálogo real: nunca inventa módulos.
      </p>
    </div>
  );
}
